import React from 'react'
import styled from 'styled-components'

import {
    BigformActionedAction,
    InvoiceGeneratorActionedField,
    invoiceGeneratorActioned,
} from '@/analytics/segment'
import type {ClearbitCompany} from '@/components/company-input/company-input'
import CompanyInput from '@/components/company-input/company-input'
import Select from '@/components/select'
import useCountryList from '@/hooks/use-country-list'
import type {ClearError, Errors, SetError} from '@/hooks/use-form-errors'
import {useGlobalStrings} from '@/providers/global-strings'
import {colors, tokens} from '@/theme'
import {convertBlobToBase64} from '@/utils/convert-blob-to-base64'
import mq from '@/utils/media-query'

import {SectionWrapper, StyledInput} from './shared-styles'
import type {Dispatch, State, StateKey} from './state'
import {Actions} from './state'

interface Props {
    statePrefix: 'your' | 'recipients'
    title: string
    dispatch: Dispatch
    state: State
    errors: Errors<State>
    setError: SetError<State>
    clearError: ClearError<State>
}

export const CompanySection = ({
    dispatch,
    state,
    statePrefix,
    title,
    errors,
    setError,
    clearError,
}: Props) => {
    const globalStrings = useGlobalStrings()
    const countryList = useCountryList()

    const handleInputChange = (key: StateKey, value: string) => {
        dispatch({
            type: Actions.SET_VALUES,
            key,
            value,
        })

        invoiceGeneratorActioned({
            action: BigformActionedAction.Interacted,
            field:
                statePrefix === 'your'
                    ? InvoiceGeneratorActionedField.YourCompanyDetails
                    : InvoiceGeneratorActionedField.RecipientDetails,
        })

        if (!value) {
            setError(key, globalStrings.this_field_is_required)
        }

        clearError(key)
    }

    const handleCompanyInputSelect = async (company: ClearbitCompany) => {
        dispatch({
            type: Actions.SET_VALUES,
            key: `${statePrefix}CompanyName`,
            value: company.name,
        })

        if (company.logo) {
            fetch(company.logo)
                .then((response) => response.blob())
                .then((blob) => {
                    convertBlobToBase64(blob, (base64string) => {
                        dispatch({
                            type: Actions.SET_VALUES,
                            key: 'yourCompanyLogo',
                            value: base64string,
                        })
                    })
                })
        }
    }

    return (
        <Wrapper title={title}>
            <FieldsWrapper>
                <FieldWrapper $width="FULL">
                    <StyledCompanyInput
                        placeholder=""
                        label={globalStrings.company_name}
                        value={state[`${statePrefix}CompanyName`]}
                        onChange={(companyName) => {
                            handleInputChange(`${statePrefix}CompanyName`, companyName)
                        }}
                        onSelect={(company: ClearbitCompany) => {
                            handleCompanyInputSelect(company)
                        }}
                        error={errors[`${statePrefix}CompanyName`]}
                        required
                    />
                </FieldWrapper>

                <FieldWrapper $width="FULL">
                    <StyledInput
                        label={globalStrings.email}
                        value={state[`${statePrefix}Email`]}
                        onChange={(value) => handleInputChange(`${statePrefix}Email`, value)}
                        error={errors[`${statePrefix}Email`]}
                        required
                    />
                </FieldWrapper>

                <FieldWrapper $width="FULL">
                    <StyledInput
                        label={globalStrings.address}
                        value={state[`${statePrefix}Address`]}
                        onChange={(value) => handleInputChange(`${statePrefix}Address`, value)}
                        error={errors[`${statePrefix}Address`]}
                        required
                    />
                </FieldWrapper>

                <FieldWrapper $width="FULL">
                    <StyledInput
                        label={globalStrings.city}
                        value={state[`${statePrefix}City`]}
                        onChange={(value) => handleInputChange(`${statePrefix}City`, value)}
                        error={errors[`${statePrefix}City`]}
                        required
                    />
                </FieldWrapper>

                <FieldWrapper $width="FULL">
                    <StyledInput
                        label={globalStrings.postcode}
                        value={state[`${statePrefix}ZipCode`]}
                        onChange={(value) => handleInputChange(`${statePrefix}ZipCode`, value)}
                        error={errors[`${statePrefix}ZipCode`]}
                        required
                    />
                </FieldWrapper>

                <FieldWrapper $width="HALF">
                    <StyledInput
                        label={globalStrings.state}
                        value={state[`${statePrefix}State`]}
                        onChange={(value) => handleInputChange(`${statePrefix}State`, value)}
                        error={errors[`${statePrefix}State`]}
                        required
                    />
                </FieldWrapper>

                <FieldWrapper $width="HALF">
                    <StyledSelect
                        label={globalStrings.country}
                        options={countryList.map(({code, name}) => ({label: name, value: code}))}
                        placeholder={globalStrings.select}
                        value={state[`${statePrefix}Country`]}
                        onChange={(value) => handleInputChange(`${statePrefix}Country`, value)}
                        error={errors[`${statePrefix}Country`]}
                        required
                    />
                </FieldWrapper>
            </FieldsWrapper>
        </Wrapper>
    )
}

const FieldWidth = {
    FULL: 'full',
    HALF: 'half',
} as const

interface FieldWrapperProps {
    $width: keyof typeof FieldWidth
}

const Wrapper = styled(SectionWrapper)`
    display: grid;
    gap: ${tokens.spacing20};
`

const FieldsWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        grid-template-columns: 1fr 1fr;
    }
`

const FieldWrapper = styled.div<FieldWrapperProps>`
    ${mq.min('sm')} {
        grid-column: ${(props) => (props.$width === 'FULL' ? '1 / span 2' : 'auto')};
    }
`

const StyledSelect = styled(Select)`
    & button {
        background-color: ${colors.background.default};
    }
`

const StyledCompanyInput = styled(CompanyInput)`
    & input {
        background-color: ${colors.background.default};
    }
`
